/**
 * E07-F12 — Jev admin (Settings → Jev): types + pure view-model helpers.
 *
 * Spec: docs/superpowers/specs/2026-10-20-research-jev-triage-design.md §5.2, §5.6, §5.7.
 *
 * Shapes here are the contract both adapters serve (`mock/jev.ts`, `real/jev.ts`).
 * The only rule logic is `lintQuestionSet` — the client-side pre-check of a question
 * set's FORM: structural checks plus the very `validateJevQuestionSet` the server
 * runs on save (`jev_decide.py lint` subset: yes/no → exactly two criteria; score →
 * at least two anchored levels; English text). The server re-checks on save; this
 * mirror only lets the editor name the error before the round-trip.
 */
import {
  jevBandSchema,
  validateJevQuestionSet,
  type JevBand as ApiJevBand,
  type JevQuestion as ApiJevQuestion,
  type JevQuestions as ApiJevQuestions,
  type JevSetIssue,
} from '@eer/shared';

// ---------------------------------------------------------------------------
// Connection (T01)
// ---------------------------------------------------------------------------

/** Which path produces AI-check verdicts (§5.4 rollback switch). */
export type JevVerdictPath = 'jev' | 'haiku';

export interface JevSettings {
  /** True when an OpenRouter key is stored. The key itself never leaves the server. */
  readonly hasKey: boolean;
  /** Masked tail, e.g. `sk-or-v1-…9f3a`. Null when no key. */
  readonly maskedKey: string | null;
  readonly enabled: boolean;
  readonly verdictPath: JevVerdictPath;
  readonly updatedAt: string;
  readonly updatedBy: string;
}

export type JevKeyAction = { kind: 'keep' } | { kind: 'set'; value: string } | { kind: 'clear' };

export interface JevSettingsUpdate {
  readonly key: JevKeyAction;
  readonly enabled: boolean;
  readonly verdictPath: JevVerdictPath;
}

/** "Jev is not configured" — no key or switched off (§5.2: every Jev button hides/greys). */
export function isJevReady(s: JevSettings | undefined): boolean {
  return Boolean(s && s.hasKey && s.enabled);
}

// ---------------------------------------------------------------------------
// Question sets (T02)
// ---------------------------------------------------------------------------

/** Answer kind — the protocol's `type`: yes/no, choice, score. */
export type JevAnswerKind = 'noul' | 'choice' | 'score';

export const ANSWER_KIND_LABEL: Record<JevAnswerKind, string> = {
  noul: 'Yes / no',
  choice: 'Choice',
  score: 'Score',
};

export interface JevCriterion {
  /** For yes/no: `yes` / `no`. For choice: the option key. For score: the level (display only — order is the level). */
  readonly key: string;
  /** What this answer means (an anchor for score levels). */
  readonly anchor: string;
}

export interface JevQuestion {
  /** Stable machine name sent to Jev (`company_type`, `grade`, …). */
  readonly name: string;
  readonly kind: JevAnswerKind;
  readonly instructions: string;
  readonly criteria: readonly JevCriterion[];
}

/** Bands the Research UI shows (check result, metrics). Set band rules may name others. */
export type JevBand = 'check_first' | 'doubtful' | 'probable_junk' | 'needs_human';

export const BAND_LABEL: Record<JevBand, string> = {
  check_first: 'Check first',
  doubtful: 'Doubtful',
  probable_junk: 'Probable junk',
  needs_human: 'Needs human review',
};

/**
 * One band rule of a set, as stored (`jev_question_sets.bands[]`): the band name, the
 * fallback flag (exactly one, the last), and the conditions as JSON text —
 * `{ "<question>": { "lte" | "gte" | "winner_in" | "top_gte" | "confidence_gte": … },
 *    "agree": [{ "choice", "noul", "positive", "gte"?, "lte"? }] }` (jev_decide.py grammar).
 */
export interface JevBandRule {
  readonly name: string;
  readonly isDefault: boolean;
  /** JSON object text; '' = no conditions (the fallback band). */
  readonly when: string;
}

/** One state truncation limit of a set (`stateCaps`), e.g. `pageChars` = 16000. Names are fixed by code. */
export interface JevLimit {
  readonly name: string;
  readonly value: number;
}

export type JevLimits = readonly JevLimit[];

export type JevSetKey = 'research_triage' | 'research_verdict' | 'research_same_company';

export interface JevQuestionSet {
  readonly key: JevSetKey;
  readonly title: string;
  readonly purpose: string;
  readonly questions: readonly JevQuestion[];
  readonly bandRules: readonly JevBandRule[];
  readonly limits: JevLimits;
  /** Calibrated on labels (false ⇒ safe defaults: junk bin off, default needs_human). */
  readonly calibrated: boolean;
  readonly calibratedAt: string | null;
  readonly updatedAt: string;
  readonly updatedBy: string;
}

export interface JevLintError {
  /** Where: `question:<index>` / `limits` / `rules:<index>` / `set`. */
  readonly where: string;
  readonly message: string;
}

/** The stored (wire) form of a set's editable part — what PUT /admin/jev/question-sets/:key takes. */
export interface JevQuestionSetBody {
  readonly questions: ApiJevQuestions;
  readonly bands: ApiJevBand[];
  readonly stateCaps: Record<string, number>;
}

const CYRILLIC = /[Ѐ-ӿ]/;
const NAME_RE = /^[a-z][a-z0-9_]*$/;

/** yes/no criteria keys → the protocol's `true` / `false`. */
function noulKey(key: string): string {
  const k = key.trim().toLowerCase();
  if (k === 'yes' || k === 'true') return 'true';
  if (k === 'no' || k === 'false') return 'false';
  return key.trim();
}

/** View question → wire question (`criteria`: record for yes/no and choice, anchor list for score). */
export function questionToApi(q: JevQuestion): ApiJevQuestion {
  const criteria =
    q.kind === 'score'
      ? q.criteria.map((c) => c.anchor)
      : Object.fromEntries(
          q.criteria.map((c) => [q.kind === 'noul' ? noulKey(c.key) : c.key.trim(), c.anchor]),
        );
  return { type: q.kind, instructions: q.instructions, criteria };
}

/** Parse a band rule's JSON condition text. `undefined` = empty; `null` = not a JSON object. */
export function parseBandWhen(text: string): Record<string, unknown> | undefined | null {
  if (!text.trim()) return undefined;
  try {
    const v: unknown = JSON.parse(text);
    return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/** View band rule → wire band. Throws only on unparsable JSON (lint reports it first). */
export function bandToApi(r: JevBandRule): ApiJevBand {
  const when = parseBandWhen(r.when);
  if (when === null) throw new Error(`Band rule ${r.name}: the condition is not a JSON object.`);
  return {
    name: r.name.trim(),
    ...(r.isDefault ? { default: true } : {}),
    ...(when !== undefined ? { when: when as ApiJevBand['when'] } : {}),
  };
}

/** The editable part of a set in its wire form (questions keep their order). */
export function questionSetToApi(
  set: Pick<JevQuestionSet, 'questions' | 'bandRules' | 'limits'>,
): JevQuestionSetBody {
  return {
    questions: Object.fromEntries(set.questions.map((q) => [q.name.trim(), questionToApi(q)])),
    bands: set.bandRules.map(bandToApi),
    stateCaps: Object.fromEntries(set.limits.map((l) => [l.name, l.value])),
  };
}

/**
 * Server shape-check issues (`validateJevQuestionSet`, the jev_decide.py lint subset) that the
 * local checks below do not already cover, in plain words. Codes not listed here keep the
 * server's own detail.
 */
const ISSUE_TEXT: Record<string, (label: string) => string> = {
  'compound-question': (l) =>
    `${l}: one question = one judgement — the instruction asks more than one thing.`,
  'score-bare-number': (l) => `${l}: a score level must be an anchor sentence, not a bare number.`,
  'criterion-repeats-label': (l) =>
    `${l}: a criterion only repeats its answer name — describe when to give that answer.`,
  'choice-without-control': (l) =>
    `${l}: an option like "none" / "other" needs a control yes/no question in a band rule "agree" check.`,
};
/** Issue codes the local checks already report (no duplicates in the list). */
const COVERED = new Set([
  'no-questions',
  'bad-type',
  'empty-instructions',
  'not-english',
  'noul-criteria',
  'choice-options',
  'score-criteria',
  'empty-criterion',
]);

/** Map one server issue to an editor error (question issues land on their question card). */
export function issueToLintError(
  issue: JevSetIssue,
  questions: readonly Pick<JevQuestion, 'name'>[],
): JevLintError {
  const m = /^([^.:\s]+)[.:]/.exec(issue.detail);
  const idx = m ? questions.findIndex((q) => q.name.trim() === m[1]) : -1;
  if (idx >= 0) {
    const label = questions[idx]!.name.trim();
    const text = ISSUE_TEXT[issue.code];
    return { where: `question:${idx}`, message: text ? text(label) : issue.detail };
  }
  if (issue.code === 'bands-default') {
    return {
      where: 'rules:0',
      message: 'Band rules: exactly one fallback band, and it must be the last one.',
    };
  }
  return {
    where: issue.code.startsWith('band') ? 'rules:0' : 'set',
    message: issue.code.startsWith('band') ? `Band rules: ${issue.detail}` : issue.detail,
  };
}

/**
 * Pre-check of a set's form. Returns every problem, in display order; empty ⇒ OK.
 * Structural checks first, then the SAME shape check the server runs on save
 * (`validateJevQuestionSet` from @eer/shared = the jev_decide.py lint subset).
 */
export function lintQuestionSet(
  set: Pick<JevQuestionSet, 'questions' | 'bandRules' | 'limits'>,
): JevLintError[] {
  const errors: JevLintError[] = [];
  if (set.questions.length === 0) {
    errors.push({ where: 'set', message: 'A set needs at least one question.' });
  }
  const seen = new Set<string>();
  set.questions.forEach((q, i) => {
    const where = `question:${i}`;
    const label = q.name.trim() || `Question ${i + 1}`;
    if (!NAME_RE.test(q.name.trim())) {
      errors.push({
        where,
        message: `${label}: the name must be snake_case Latin (letters, digits, underscores).`,
      });
    } else if (seen.has(q.name.trim())) {
      errors.push({ where, message: `${label}: two questions have the same name.` });
    }
    seen.add(q.name.trim());
    if (!q.instructions.trim()) {
      errors.push({ where, message: `${label}: the instruction is empty.` });
    }
    const texts = [q.instructions, ...q.criteria.flatMap((c) => [c.key, c.anchor])];
    if (texts.some((t) => CYRILLIC.test(t))) {
      errors.push({
        where,
        message: `${label}: questions must be in English — Cyrillic text found. Request data goes into the state, not into the question.`,
      });
    }
    if (q.kind === 'noul') {
      const keys = q.criteria.map((c) => noulKey(c.key)).sort();
      if (q.criteria.length !== 2) {
        errors.push({
          where,
          message: `${label}: a yes/no question needs exactly two criteria (yes and no), it has ${q.criteria.length}.`,
        });
      } else if (keys[0] !== 'false' || keys[1] !== 'true') {
        errors.push({ where, message: `${label}: the two answers must be named "yes" and "no".` });
      }
    }
    if (q.kind === 'choice') {
      if (q.criteria.length < 2) {
        errors.push({ where, message: `${label}: a choice question needs at least two options.` });
      }
      const keys = q.criteria.map((c) => c.key.trim()).filter(Boolean);
      if (new Set(keys).size !== keys.length) {
        errors.push({ where, message: `${label}: two options have the same name.` });
      }
    }
    if (q.kind === 'score' && q.criteria.length < 2) {
      errors.push({ where, message: `${label}: a score question needs at least two levels.` });
    }
    if (q.criteria.some((c) => !c.anchor.trim())) {
      errors.push({
        where,
        message:
          q.kind === 'score'
            ? `${label}: every score level needs an anchor.`
            : `${label}: every answer needs a criterion (when to give it).`,
      });
    }
    if (q.kind !== 'score' && q.criteria.some((c) => !c.key.trim())) {
      errors.push({ where, message: `${label}: an answer option has no name.` });
    }
  });

  let bandsOk = true;
  set.bandRules.forEach((r, i) => {
    const where = `rules:${i}`;
    const label = `Band rule ${i + 1}${r.name.trim() ? ` (${r.name.trim()})` : ''}`;
    if (!r.name.trim()) {
      errors.push({ where, message: `${label}: the band name is empty.` });
    }
    const when = parseBandWhen(r.when);
    if (when === null) {
      bandsOk = false;
      errors.push({ where, message: `${label}: the condition is not a valid JSON object.` });
      return;
    }
    if (when === undefined && !r.isDefault) {
      errors.push({
        where,
        message: `${label}: the condition is empty — only the fallback band has none.`,
      });
    }
    try {
      if (!jevBandSchema.safeParse(bandToApi(r)).success) {
        errors.push({
          where,
          message: `${label}: unknown condition key or wrong value (allowed: lte, gte, winner_in, top_gte, confidence_gte, agree).`,
        });
        bandsOk = false;
      }
    } catch {
      bandsOk = false;
    }
  });

  set.limits.forEach((l) => {
    if (!Number.isInteger(l.value) || l.value < 0) {
      errors.push({ where: 'limits', message: `Limit ${l.name}: must be a whole number ≥ 0.` });
    }
  });

  // The server's own shape check, on the wire form (exact parity with what PUT validates).
  {
    const body = questionSetToApi({
      questions: set.questions,
      bandRules: bandsOk ? set.bandRules : [],
      limits: set.limits,
    });
    for (const issue of validateJevQuestionSet({
      questions: body.questions,
      ...(bandsOk ? { bands: body.bands } : {}),
    })) {
      if (COVERED.has(issue.code)) continue;
      errors.push(issueToLintError(issue, set.questions));
    }
  }
  return errors;
}

/** Default criteria when the admin switches a question to another answer kind. */
export function defaultCriteria(kind: JevAnswerKind): JevCriterion[] {
  if (kind === 'noul') {
    return [
      { key: 'yes', anchor: '' },
      { key: 'no', anchor: '' },
    ];
  }
  if (kind === 'score') {
    return [
      { key: '0', anchor: '' },
      { key: '1', anchor: '' },
    ];
  }
  return [
    { key: 'option_a', anchor: '' },
    { key: 'option_b', anchor: '' },
  ];
}

// ---------------------------------------------------------------------------
// Thresholds (T03)
// ---------------------------------------------------------------------------

export interface JevThresholdValue {
  readonly name: string;
  readonly label: string;
  /** Confidence / probability, 0..1. Null ⇒ off (e.g. junk bin before calibration). */
  readonly value: number | null;
}

export type JevVersionSource = 'auto' | 'rollback' | 'reset' | 'recalibration' | 'initial';

export const VERSION_SOURCE_LABEL: Record<JevVersionSource, string> = {
  auto: 'Auto-recalculated',
  rollback: 'Rollback',
  reset: 'Reset to safe defaults',
  recalibration: 'Recalibration',
  initial: 'Initial calibration',
};

export interface JevThresholdVersion {
  readonly version: number;
  readonly createdAt: string;
  readonly source: JevVersionSource;
  readonly reason: string;
  readonly values: readonly JevThresholdValue[];
}

export interface JevSafeguardWarning {
  readonly at: string;
  readonly message: string;
}

export interface JevRecalibrationJob {
  readonly startedAt: string;
  readonly rows: number;
  readonly done: number;
}

export interface JevSetThresholds {
  readonly setKey: JevSetKey;
  readonly currentVersion: number;
  readonly frozen: boolean;
  readonly positives: number;
  readonly labels: number;
  readonly versions: readonly JevThresholdVersion[];
  readonly warnings: readonly JevSafeguardWarning[];
  /** A running recalibration (paid, started by the admin), if any. */
  readonly recalibration: JevRecalibrationJob | null;
}

/** Positives needed before the junk bin may switch on (§5.6). */
export const JUNK_MIN_POSITIVES = 20;

export interface JevRecalibrationQuote {
  readonly setKey: JevSetKey;
  /** Labelled rows that need a fresh Jev call. */
  readonly rows: number;
  readonly costPerRowUsd: number;
  readonly totalUsd: number;
  /** True when the per-row price is a constant (no call history yet). */
  readonly isEstimate: boolean;
}

// ---------------------------------------------------------------------------
// Metrics + set check (T04)
// ---------------------------------------------------------------------------

export interface JevSetMetrics {
  readonly setKey: JevSetKey;
  readonly decisions: number;
  readonly labels: number;
  /** Share of labelled decisions where Jev agreed with the human, 0..1. Null ⇒ no labels. */
  readonly agreement: number | null;
  readonly junkShare: number;
  readonly needsHumanShare: number;
  readonly costUsd: number;
}

export interface JevMetrics {
  readonly fromDate: string;
  readonly toDate: string;
  readonly sets: readonly JevSetMetrics[];
}

export interface JevCheckRow {
  readonly id: string;
  readonly requestId: string;
  readonly requestTitle: string;
  readonly company: string;
  readonly domain: string;
  readonly city: string;
  readonly state: string;
  readonly employees: string;
}

export interface JevCheckAnswer {
  readonly question: string;
  readonly kind: JevAnswerKind;
  /** The top answer (option key / yes / no / the score). */
  readonly answer: string;
  readonly confidence: number;
  /** Probability per option (yes/no and choice) or per level (score). */
  readonly probabilities: readonly { readonly key: string; readonly p: number }[];
}

export interface JevCheckResult {
  readonly setKey: JevSetKey;
  readonly rowId: string;
  readonly answers: readonly JevCheckAnswer[];
  readonly band: JevBand | null;
  readonly reason: string;
  readonly costUsd: number;
  readonly latencyMs: number;
  readonly stateChars: number;
}

export interface JevCheckQuote {
  readonly costUsd: number;
  readonly isEstimate: boolean;
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

const DATE_FMT = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});
const DATETIME_FMT = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

export function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : DATE_FMT.format(d);
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : DATETIME_FMT.format(d);
}

/** `$0.35`, `$12.40`; sub-cent amounts keep 4 decimals (`$0.0042`). */
export function formatUsd(v: number): string {
  if (v > 0 && v < 0.01) return `$${v.toFixed(4)}`;
  return `$${v.toFixed(2)}`;
}

export function formatProb(v: number | null): string {
  return v === null ? 'off' : v.toFixed(2);
}

export function formatPct(v: number | null): string {
  return v === null ? '—' : `${Math.round(v * 1000) / 10}%`;
}

export function formatInt(v: number): string {
  return v.toLocaleString('en-US');
}
